import { useState, useRef, useEffect, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { languages, STORAGE_KEY, type LanguageCode } from '@/i18n/config';
import { FlagImg } from '@/components/ui/CountryFlag';

export const LanguageSelector = forwardRef<HTMLDivElement>(function LanguageSelector(_props, ref) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (code: LanguageCode) => {
    i18n.changeLanguage(code);
    // Save manual selection - this prevents IP auto-detection from overriding
    localStorage.setItem(STORAGE_KEY, code);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-background/80 backdrop-blur-sm hover:bg-secondary transition-colors text-sm"
        aria-label="Select language"
      >
        <FlagImg code={currentLanguage.iso2} size="sm" />
        <span className="font-medium hidden sm:inline">{currentLanguage.code.toUpperCase()}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-card rounded-xl shadow-xl border border-border z-50 overflow-hidden animate-fade-in-up">
          {languages.map((language) => {
            const isSelected = i18n.language === language.code;
            return (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left ${
                  isSelected ? 'bg-accent/10' : 'hover:bg-secondary'
                }`}
              >
                <FlagImg code={language.iso2} size="sm" />
                <span className="font-medium flex-1">{language.name}</span>
                {isSelected && <Check className="w-4 h-4 text-accent" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
